import type { ComponentPropsWithoutRef } from "react";
import type { Control } from "react-hook-form";
import { t } from "ttag";

import type { Session } from "@/features/coaching/models";
import { Form, Select } from "@/shared/ui";
import { getTimeSlotOptions } from "@/shared/utils";

const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

type SessionStartTimeControl<T> =
  T extends Pick<Session, "startAt"> ? Control<T, "startAt"> : never;

type SessionStartTimeFieldProps<T extends Pick<Session, "startAt">> =
  ComponentPropsWithoutRef<typeof Form.Item> & {
    control: SessionStartTimeControl<T>;
  };

export const SessionStartTimeField = <T extends Pick<Session, "startAt">>({
  control,
  ...props
}: SessionStartTimeFieldProps<T>) => {
  return (
    <Form.Field
      control={control}
      name="startAt"
      render={({ field: { value, onChange, ...field } }) => {
        const dateValue = value instanceof Date ? value : new Date();
        const options = getTimeSlotOptions(dateValue).map((option) => ({
          ...option,
          value: option.value.toString(),
        }));

        return (
          <Form.Item {...props}>
            <Form.Label aria-required>
              {t`Time`} ({localTimeZone.replace("_", " ")})
            </Form.Label>
            <Form.Control>
              <Select
                options={options}
                value={dateValue === value ? value.toString() : ""}
                onChange={(timeSlot) => {
                  onChange(new Date(timeSlot));
                }}
                {...field}
              />
            </Form.Control>
            <Form.Message compact={false} />
          </Form.Item>
        );
      }}
    />
  );
};
